import { avg, pct, rating, record } from '../format';
import type { Comparison, RatedPlayer } from '../types';

const cells = (player: RatedPlayer | null) => {
  if (!player) return { name: '—', matches: '–', legs: '–', average: '–', score: '–' };
  // A player with no record anywhere has nothing to show — printing "0:0" would
  // read as "played and lost nothing" rather than "never played".
  if (player.statsSource === 'none') {
    return {
      name: player.displayName,
      matches: '–',
      legs: '–',
      average: '–',
      score: rating(player.rating.strength),
    };
  }
  return {
    name: player.displayName,
    matches: record(player.singleWon, player.singleLost),
    legs: record(player.singleLegsWon, player.singleLegsLost),
    average: avg(player.average),
    score: rating(player.rating.strength),
  };
};

/**
 * The head-to-head table: the three figures the brief asks for — single matches,
 * single legs and average — for both columns, plus the weighted score they roll
 * into and the resulting win probability.
 */
export function ComparisonTable({ comparison }: { comparison: Comparison }) {
  const { pairings, expectedWins, logisticK, effectiveLogisticK } = comparison;
  const flattened = effectiveLogisticK < logisticK;
  const selected = [...comparison.left, ...comparison.right];
  const carried = selected.filter((p) => p.statsSource === 'carried').length;
  const manual = selected.filter((p) => p.statsSource === 'manual').length;
  const unplayed = selected.filter((p) => p.statsSource === 'none').length;

  return (
    <section className="card">
      <header>
        <h2>Head to head</h2>
        <p className="hint">
          Rating = 3-dart average + 8 × (leg share − ½), in points · logistic k ={' '}
          {flattened ? (
            <span title="Flattened because the ratings rest on carried-over data">
              {effectiveLogisticK.toFixed(2)} (of {logisticK})
            </span>
          ) : (
            logisticK
          )}
        </p>
      </header>

      {carried > 0 && (
        <div className="notice">
          {carried} selected {carried === 1 ? 'player is' : 'players are'} rated on their previous
          season because they have not played in this one yet. Form carries over imperfectly — treat
          it as a starting estimate.
        </div>
      )}
      {manual > 0 && (
        <div className="notice">
          {manual} selected {manual === 1 ? 'player uses' : 'players use'} hand-entered statistics.
        </div>
      )}
      {unplayed > 0 && (
        <div className="notice">
          {unplayed} selected {unplayed === 1 ? 'player has' : 'players have'} no recorded games at
          all, so {unplayed === 1 ? 'their score falls' : 'their scores fall'} back to the league
          average. Predictions involving them carry no real information.
        </div>
      )}

      {pairings.length === 0 ? (
        <div className="empty-state">Select players on both sides to compare them.</div>
      ) : (
        <>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th className="left">#</th>
                  <th className="left">Player</th>
                  <th>Matches</th>
                  <th>Legs</th>
                  <th>Average</th>
                  <th title="Rating in points: a 3-dart average nudged by leg share">Rating</th>
                  <th style={{ width: 130 }}>Win chance</th>
                  <th title="Rating in points: a 3-dart average nudged by leg share">Rating</th>
                  <th>Average</th>
                  <th>Legs</th>
                  <th>Matches</th>
                  <th className="left">Player</th>
                </tr>
              </thead>
              <tbody>
                {pairings.map((pairing) => {
                  const left = cells(pairing.left);
                  const right = cells(pairing.right);
                  const p = pairing.leftWinProbability;
                  return (
                    <tr key={pairing.index}>
                      <td className="left muted">{pairing.index + 1}</td>
                      <td className="left">{left.name}</td>
                      <td className="num">{left.matches}</td>
                      <td className="num">{left.legs}</td>
                      <td className="num">{left.average}</td>
                      <td className="num">{left.score}</td>
                      <td>
                        {p === null ? (
                          <span className="muted">no pair</span>
                        ) : (
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <div className="split" style={{ flex: 1 }}>
                              <span style={{ width: `${p * 100}%` }} />
                            </div>
                            <span className="num" style={{ minWidth: 52 }}>
                              {pct(p, 0)}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="num">{right.score}</td>
                      <td className="num">{right.average}</td>
                      <td className="num">{right.legs}</td>
                      <td className="num">{right.matches}</td>
                      <td className="left">{right.name}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="legend">
            <span>
              <i style={{ background: 'var(--home)' }} /> left column — expected wins{' '}
              <strong>{expectedWins.left.toFixed(2)}</strong>
            </span>
            <span>
              <i style={{ background: 'var(--away)' }} /> right column — expected wins{' '}
              <strong>{expectedWins.right.toFixed(2)}</strong>
            </span>
          </div>
        </>
      )}
    </section>
  );
}
